/**
 * ItemsController — /api/v1/items
 * Mirrors: src/Aidepo.Api/Controllers/ItemsController.cs
 */
import { apiDelete, apiGet, apiPatch, apiPost } from '../client.js';
import type { IsoDateTime, Uuid } from '../types.js';

export interface ItemSpec {
  readonly name: string;
  readonly quantity?: number;
  /**
   * Manually picked icon name (from tokens ICON_NAMES). Omit or pass null to
   * let the back-end / front-end auto-resolve the icon from the name (hh-345).
   */
  readonly icon?: string | null;
  /**
   * Free-text placement detail below room level (case-40), e.g. "верхний ящик
   * стола". Omit when not specified.
   */
  readonly locationNote?: string | null;
}

export interface AddItemsRequest {
  readonly items: readonly ItemSpec[];
  readonly roomId?: Uuid;
  readonly roomNameHint?: string;
  readonly propertyId?: Uuid;
  readonly propertyNameHint?: string;
  /**
   * When true, items are created without a place (RoomId/PropertyId null) as
   * orphans (case-06); the room/property hints above are ignored by the
   * back-end. Omit or pass false to require a place as usual (aidepo-57.7).
   */
  readonly orphan?: boolean;
}

export interface ItemCreated {
  readonly id: Uuid;
  readonly name: string;
  readonly quantity: number;
  /** Resolved icon name, or null when left to auto-resolve (hh-345). */
  readonly icon?: string | null;
  /** Placement detail below room level (case-40), or null. */
  readonly locationNote?: string | null;
}

export interface AddItemsResponse {
  readonly items: readonly ItemCreated[];
  readonly location: string;
}

export interface SearchItemHit {
  readonly id: Uuid;
  readonly name: string;
  readonly quantity: number;
  /** Location label, including the placement detail in parentheses when set. */
  readonly location: string;
  /** Manually picked icon name, or null/undefined for auto-resolve (hh-345). */
  readonly icon?: string | null;
  /** Free-text placement detail below room level (case-40), or null. */
  readonly locationNote?: string | null;
}

export interface SearchItemsResponse {
  readonly query: string;
  readonly count: number;
  readonly items: readonly SearchItemHit[];
}

export interface OrphanItem {
  readonly id: Uuid;
  readonly name: string;
  readonly quantity: number;
  readonly isOrphan: true;
  readonly location: string;
  /** Manually picked icon name, or null/undefined for auto-resolve (hh-345). */
  readonly icon?: string | null;
  /** Free-text placement detail below room level (case-40), or null. */
  readonly locationNote?: string | null;
  /**
   * Item creation timestamp (ISO 8601). Projected for the «Недавно добавленные»
   * sort on the flat items list (aidepo-63.6). May arrive as `undefined` from
   * older payloads — treat as "unknown / oldest".
   */
  readonly createdAt?: IsoDateTime;
}

export interface OrphanItemsResponse {
  readonly count: number;
  readonly items: readonly OrphanItem[];
}

export interface UpdateItemRequest {
  readonly name?: string;
  readonly quantity?: number;
  /**
   * Icon override (hh-345). Field absent → icon not changed; a non-empty
   * string → manual icon set; `null` → reset to auto-resolve.
   */
  readonly icon?: string | null;
  /**
   * Placement detail below room level (case-40). Field absent → unchanged;
   * empty string → cleared; non-empty string → set.
   */
  readonly locationNote?: string;
}

export interface UpdateItemResponse {
  readonly id: Uuid;
  readonly name: string;
  readonly quantity: number;
  /** Resolved icon name, or null when left to auto-resolve (hh-345). */
  readonly icon?: string | null;
  /** Placement detail below room level (case-40), or null. */
  readonly locationNote?: string | null;
}

export interface MoveItemRequest {
  readonly toRoomId?: Uuid;
  readonly toRoomNameHint?: string;
  readonly toPropertyId?: Uuid;
  readonly toPropertyNameHint?: string;
  /** Target container item — the moved item inherits the container's location. */
  readonly toContainerId?: Uuid;
}

export interface MoveItemResponse {
  readonly id: Uuid;
  readonly location: string;
  readonly changeRecordId: Uuid;
}

/** One row of GET /api/v1/items/{id}/history (DESC by changedAt). */
export interface ItemHistoryEntry {
  readonly id: Uuid;
  readonly fromRoomName: string | null;
  readonly toRoomName: string | null;
  readonly fromPropertyName: string | null;
  readonly toPropertyName: string | null;
  readonly changedAt: IsoDateTime;
  /** 'ai' — assistant-initiated move; 'user'/'manual' — человек performed it. */
  readonly actorKind: string;
  readonly changeRecordId: Uuid;
}

export interface ItemHistoryResponse {
  readonly itemId: Uuid;
  readonly count: number;
  readonly history: readonly ItemHistoryEntry[];
}

/** POST /api/v1/items — batch-add items, scoped to a room or property. */
export function addItems(req: AddItemsRequest): Promise<AddItemsResponse> {
  return apiPost<AddItemsResponse, AddItemsRequest>('/api/v1/items', req);
}

/** GET /api/v1/items?q=... — ILIKE search by name, capped at 50. */
export function searchItems(q: string): Promise<SearchItemsResponse> {
  const qs = new URLSearchParams({ q }).toString();
  return apiGet<SearchItemsResponse>(`/api/v1/items?${qs}`);
}

/** GET /api/v1/items/orphans — items with no room, property, or container (case-06). */
export function listOrphans(): Promise<OrphanItemsResponse> {
  return apiGet<OrphanItemsResponse>('/api/v1/items/orphans');
}

/** PATCH /api/v1/items/{id} — rename or change quantity. */
export function updateItem(id: Uuid, req: UpdateItemRequest): Promise<UpdateItemResponse> {
  return apiPatch<UpdateItemResponse, UpdateItemRequest>(`/api/v1/items/${id}`, req);
}

/** DELETE /api/v1/items/{id}. */
export function deleteItem(id: Uuid): Promise<void> {
  return apiDelete(`/api/v1/items/${id}`);
}

/** POST /api/v1/items/{id}/move — relocate an item to a room, property, or container. */
export function moveItem(id: Uuid, req: MoveItemRequest): Promise<MoveItemResponse> {
  return apiPost<MoveItemResponse, MoveItemRequest>(`/api/v1/items/${id}/move`, req);
}

/** GET /api/v1/items/{id}/history — move timeline, DESC by changedAt. */
export function getItemHistory(id: Uuid): Promise<ItemHistoryResponse> {
  return apiGet<ItemHistoryResponse>(`/api/v1/items/${id}/history`);
}
